import { Suspense, lazy, useEffect, useEffectEvent, useRef, useState } from "react"
import { Outlet, useSearchParams } from "react-router-dom"

import Box from "@mui/material/Box"
import { Button, Fade } from "@mui/material"

import { CopyrightComponent } from "./components/core/CopyrightComponent"
import { handleGetLocation } from "./components/CountryCityStateFetcher"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { AppPreloader } from "./components/AppPreloader"
import { AppCheckStatusAlert } from "./components/system/AppCheckStatusAlert"
import { useLocation as useUserLocation } from "react-ui-components/components/location/locationContext"
import { auth } from "./firebaseAuth"
import { isProfilePassToken, PROFILE_PASS_PARAM } from "./profilePass/payload"
import { useErrorHandler } from "./hooks/useErrorHandler"
import { useNamespacedTranslation } from "./hooks/useNamespacedTranslation"
import { useSharedRaffleLink } from "./hooks/useSharedRaffleLink"
import SidebarComponent from "./SidebarComponent"
import { useAuthState } from "react-firebase-hooks/auth"
import {
  applyUrlContext,
  hasLocationContext,
  normalizeLanguageCode,
  normalizeLocationContext,
  readUrlContext,
} from "./utils/urlContext"
import { qrFeedbackPopupStyle } from "./utils/popupStyles"
import { POINT_TRANSFER_CODE_PARAM } from "./utils/pointsTransfer"
import { captureReferralCodeFromLocation, consumeStoredReferralCode } from "./services/referralService"
import {
  openExchangePointsPopup,
  openLoginPopup,
  openQrProcessingPopup,
} from "./utils/lazyPopups"

const DRAWER_WIDTH = 240

const TOAST_CONTAINER_PROPS = {
  position: "bottom-right" as const,
  autoClose: 5000,
  hideProgressBar: false,
  newestOnTop: true,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: "light" as const,
  style: { zIndex: 9999 },
}

const LazyToastContainer = lazy(() =>
  import("react-toastify").then(({ ToastContainer }) => ({ default: ToastContainer })),
)
const LazyWhatsNew = lazy(() => import("./profilePass/WhatsNew"))
const LazyVendorPass = lazy(() => import("./profilePass/VendorPass"))
const LazyCommentsHost = lazy(() => import("./comments/CommentsHost"))

const LazyShareModal = lazy(() =>
  import("react-ui-components/components/ShareModal").then((module) => ({
    default: module.default,
  })),
)

// Tracks the code currently being handled so Strict Mode remounts do not process it twice.
let activeQrCode: string | null = null
let activePointTransferCode: string | null = null

const getBrowserBaseLanguage = (): string => {
  if (typeof navigator === "undefined") {
    return "en"
  }

  return navigator.language?.split?.("-")?.[0] || navigator.language || "en"
}

const getSearchSnapshot = (searchParams: URLSearchParams) =>
  new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : searchParams.toString(),
  )

const removeSearchParam = (searchParams: URLSearchParams, key: string) => {
  const nextParams = new URLSearchParams(searchParams)
  nextParams.delete(key)
  return nextParams
}

function App() {
  const { i18n, ready, t } = useNamespacedTranslation(["app", "error", "common"])
  const [user, loading] = useAuthState(auth)
  const { handleError } = useErrorHandler("App")
  const { currentLocation } = useUserLocation()
  // `zip` isn't on the currently published ILocationItem type yet - cast until the
  // react-ui-components dependency pin picks up the ZIP work.
  const userZip = (currentLocation as { zip?: string } | undefined)?.zip || ""
  useSharedRaffleLink({ enabled: !loading && ready, userZip })
  const [mountToastContainer, setMountToastContainer] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedNotificationUidRef = useRef<string | null>(null)
  const code = searchParams.get("code")?.trim() || ""
  const shareCode = searchParams.get("shareCode")?.trim() || ""
  const pointTransferCode = searchParams.get(POINT_TRANSFER_CODE_PARAM)?.trim() || ""
  const profilePassToken = searchParams.get(PROFILE_PASS_PARAM) || ""
  const [vendorPassToken, setVendorPassToken] = useState("")
  // Remove bearer capability from the address bar/history before making requests.
  useEffect(() => {
    if (!profilePassToken) return
    if (isProfilePassToken(profilePassToken)) setVendorPassToken(profilePassToken)
    const params = removeSearchParam(searchParams, PROFILE_PASS_PARAM)
    setSearchParams(params, { replace: true })
  }, [profilePassToken, searchParams, setSearchParams])
  const shouldMountShareModal = Boolean(
    searchParams.get("rid") || searchParams.get("sid"),
  )
  const hasSharedRaffleLink = Boolean(searchParams.get("id")?.trim() || shareCode)

  const syncGrantedNotificationToken = useEffectEvent(async (uid: string) => {
    try {
      const hasNotificationsApi =
        typeof window !== "undefined" &&
        typeof Notification !== "undefined" &&
        typeof Notification.requestPermission === "function"

      if (!hasNotificationsApi || Notification.permission !== "granted") {
        return
      }

      const [{ doc, updateDoc }, { db }, messagingModule] = await Promise.all([
        import("firebase/firestore"),
        import("./firebaseFirestore"),
        import("./firebaseMessaging"),
      ])
      const notificationMessaging = await messagingModule.messaging()
      if (notificationMessaging === null) {
        return
      }

      const registration = await messagingModule.getMessagingServiceWorkerRegistration()
      if (!registration) {
        console.warn("Service worker not ready; skipping push subscription.")
        return
      }

      const token = await messagingModule.getToken(notificationMessaging, {
        vapidKey: process.env.REACT_APP_WEB_NOTIFICATION_KEY,
        serviceWorkerRegistration: registration,
      }).catch((error) => {
        const message = error?.message || String(error || "")
        if (message.includes("database connection is closing")) {
          console.warn("FCM token request skipped: messaging storage not ready yet.")
          return null
        }
        throw error
      })

      if (!token || !uid) {
        return
      }

      await updateDoc(doc(db, `users/${uid}`), { fcmWebToken: token })
    } catch (error) {
      handleError(error, {
        operation: "request-notification-permission",
        userMessage: t("notifications"),
      })
    }
  })

  const handleUrlCode = useEffectEvent((nextCode: string) => {
    const nextParams = removeSearchParam(searchParams, "code")

    if (user && !user.isAnonymous) {
      setSearchParams(nextParams, { replace: true })
      void openQrProcessingPopup({
        title: t("processingTicket"),
        message: t("qrScanProcessingHint", { defaultValue: "We are validating your QR code now." }),
        caption: t("qrScanProcessingCaption", { defaultValue: "This usually takes only a moment." }),
        style: qrFeedbackPopupStyle,
      })

      void (async () => {
        try {
          const { processUserCode } = await import("./processUserCode")
          processUserCode(nextCode, t)
        } catch (error) {
          handleError(error, {
            operation: "process-user-code",
            userMessage: t("error.processingCode"),
          })
        }
      })()

      return
    }

    void openLoginPopup({ message: t("Login") }).finally(() => {
      setSearchParams(nextParams, { replace: true })
    })
  })

  const handleReferralRedemption = useEffectEvent(async (uid: string) => {
    const referralCode = consumeStoredReferralCode()
    if (!referralCode) {
      return
    }

    try {
      const { redeemReferral } = await import("./services/referralService")
      await redeemReferral(referralCode)
    } catch (error) {
      // Best-effort/background: an invalid, self, or already-redeemed code should never
      // interrupt the user's session with an error popup.
      handleError(error, { operation: "redeem-referral-code", context: { uid } })
    }
  })

  const handlePointTransferCode = useEffectEvent((nextPointTransferCode: string) => {
    const nextParams = removeSearchParam(searchParams, POINT_TRANSFER_CODE_PARAM)

    if (user && !user.isAnonymous) {
      setSearchParams(nextParams, { replace: true })
      void openExchangePointsPopup(nextPointTransferCode)
      return
    }

    void openLoginPopup({ message: t("Login") }).finally(() => {
      setSearchParams(nextParams, { replace: true })
    })
  })

  useEffect(() => {
    captureReferralCodeFromLocation()
  }, [])

  useEffect(() => {
    if (loading || !user || user.isAnonymous) {
      return
    }

    // Firebase Auth sets creationTime === lastSignInTime only on the very first sign-in,
    // regardless of which auth method was used (email/password, Google, Apple, phone) — a
    // single, method-agnostic "is this a brand-new account" signal instead of duplicating
    // referral-capture logic across every sign-up/sign-in entry point. See Trello #171.
    const isNewUser =
      Boolean(user.metadata?.creationTime) &&
      user.metadata.creationTime === user.metadata.lastSignInTime

    if (!isNewUser) {
      return
    }

    void handleReferralRedemption(user.uid)
  }, [handleReferralRedemption, loading, user])

  useEffect(() => {
    const initialUrlContext = readUrlContext(
      typeof window !== "undefined" ? window.location.search : "",
    )

    if (initialUrlContext.hasLanguage) {
      i18n.changeLanguage(initialUrlContext.language)
    } else if (!i18n.language || i18n.language === "en-US") {
      i18n.changeLanguage(getBrowserBaseLanguage())
    }

    if (!initialUrlContext.hasLocation) {
      handleGetLocation({ local: true })
    }

  }, [i18n])

  useEffect(() => {
    const schedule =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (window as any).requestIdleCallback
        : (callback: () => void) => window.setTimeout(callback, 1200)
    const cancel =
      typeof window !== "undefined" && "cancelIdleCallback" in window
        ? (window as any).cancelIdleCallback
        : globalThis.clearTimeout
    const id = schedule(() => setMountToastContainer(true))
    return () => cancel(id)
  }, [])

  useEffect(() => {
    const urlContext = readUrlContext(searchParams)
    const normalizedLanguage = normalizeLanguageCode(i18n.language)

    if (urlContext.hasLanguage && urlContext.language !== normalizedLanguage) {
      return
    }

    const paramsWithContext = getSearchSnapshot(searchParams)
    const previousSearch = paramsWithContext.toString()
    const existingContext = readUrlContext(paramsWithContext)

    applyUrlContext(paramsWithContext, {
      language: normalizedLanguage,
      location: hasLocationContext(currentLocation)
        ? currentLocation
        : normalizeLocationContext(existingContext.location),
    })

    if (paramsWithContext.toString() !== previousSearch) {
      setSearchParams(paramsWithContext, { replace: true })
    }
  }, [currentLocation, i18n.language, searchParams, setSearchParams])

  useEffect(() => {
    if (loading) {
      return
    }

    if (!user) {
      requestedNotificationUidRef.current = null
      return
    }

    if (requestedNotificationUidRef.current === user.uid) {
      return
    }

    requestedNotificationUidRef.current = user.uid
    syncGrantedNotificationToken(user.uid)
  }, [loading, syncGrantedNotificationToken, user])

  useEffect(() => {
    if (!code) {
      activeQrCode = null
      return
    }

    if (loading || activeQrCode === code) {
      return
    }

    activeQrCode = code
    handleUrlCode(code)
  }, [code, handleUrlCode, loading])

  useEffect(() => {
    if (!pointTransferCode) {
      activePointTransferCode = null
      return
    }

    if (loading || activePointTransferCode === pointTransferCode) {
      return
    }

    activePointTransferCode = pointTransferCode
    handlePointTransferCode(pointTransferCode)
  }, [handlePointTransferCode, loading, pointTransferCode])

  if (loading || !ready) {
    return (
      <AppPreloader
        message={loading && ready ? t("loading", { defaultValue: "Loading..." }) : undefined}
        variant="skeleton"
      />
    )
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) =>
        handleError(error, {
          operation: "render",
          context: { componentStack: errorInfo.componentStack },
        })
      }
    >
      <Fade in timeout={500}>
        <Box className="flex flex-col relative min-h-screen">
          <AppCheckStatusAlert />
          <Suspense fallback={null}><LazyCommentsHost /></Suspense>
          <Box className="flex-1 min-h-0 overflow-auto">
            <SidebarComponent drawerWidth={DRAWER_WIDTH}>
              <Box className="px-3 pb-6 sm:px-6" data-testid="outlet">
                {mountToastContainer && user && !user.isAnonymous && !vendorPassToken && !code && !pointTransferCode && !hasSharedRaffleLink && !shouldMountShareModal ? (
                  <Suspense fallback={null}><LazyWhatsNew key={user.uid} uid={user.uid} auto /></Suspense>
                ) : null}
                {hasSharedRaffleLink ? <AppPreloader variant="skeleton" /> : <Outlet />}
              </Box>
              <Box className="px-3 pb-6 sm:px-6">
                <CopyrightComponent />
              </Box>
            </SidebarComponent>
          </Box>
          {mountToastContainer ? (
            <Suspense fallback={null}>
              <LazyToastContainer {...TOAST_CONTAINER_PROPS} />
            </Suspense>
          ) : null}
          {shouldMountShareModal ? (
            <Suspense fallback={null}>
              <LazyShareModal userZip={userZip} />
            </Suspense>
          ) : null}
          {vendorPassToken ? (
            <Suspense fallback={null}>
              {user && !user.isAnonymous ? (
                <LazyVendorPass key={`${user.uid}:${vendorPassToken}`} token={vendorPassToken} onClose={() => setVendorPassToken("")} />
              ) : (
                <Box sx={{ p: 2 }}><Button onClick={() => void openLoginPopup({ message: t("Login") })}>{t("Login")}</Button></Box>
              )}
            </Suspense>
          ) : null}
        </Box>
      </Fade>
    </ErrorBoundary>
  )
}

export default App
